import { AccountCategory, ListedInstrument, Loadable, SelectedAccountOrClient } from '@eih-netbroker/common';
import { AccordionDetails } from '@mui/material';
import { useEffect, useEffectEvent } from 'react';
import { useLocale } from '../../../../context/NbLocalizationContext';
import { NbIndefiniteLoader } from '../../../../shared/components/loader/Loader';
import { MoneyMaskHOC } from '../../../../shared/components/money-mask/MoneyMask';
import { useFormatter } from '../../../../shared/services/utils/useFormatter';
import { useCollateralSummary } from '../../../portfolio/services/useCollateralSummary';

type FuturesMaxPositionValueProps = {
  selectedAccountOrClient: SelectedAccountOrClient;
  instrument: ListedInstrument;
};

const FuturesMaxPositionValue = ({ selectedAccountOrClient, instrument }: FuturesMaxPositionValueProps) => {
  const { loadCollateralSummary, activeCollateralSummary, collateralSummary } =
    useCollateralSummary(selectedAccountOrClient);
  const { formatCurrency } = useFormatter();

  const initFuturesMaxPosition = useEffectEvent(() => {
    if (Loadable.isUninitialized(collateralSummary)) {
      loadCollateralSummary();
    }
  });

  useEffect(() => {
    initFuturesMaxPosition();
  }, []);

  if (collateralSummary.isLoading) {
    return (
      <NbIndefiniteLoader
        isContrast
        size={100}
      />
    );
  }

  if (collateralSummary.isLoaded) {
    const marginRequirementMax =
      collateralSummary.collateralSummaryAmountsByClientId.totalCollateralValue -
      collateralSummary.collateralSummaryAmountsByClientId.marginRequirement;

    if (selectedAccountOrClient.account?.accountCategory === AccountCategory.Normal) {
      return formatCurrency(marginRequirementMax, instrument.currency);
    } else {
      if (!activeCollateralSummary) {
        return '-';
      }
      return formatCurrency(
        Math.min(
          marginRequirementMax,
          activeCollateralSummary.totalCollateralValue - activeCollateralSummary.marginRequirement,
        ),
        instrument.currency,
      );
    }
  }

  return '-';
};

type FuturesMaxPositionProps = {
  selectedAccountOrClient: SelectedAccountOrClient;
  instrument: ListedInstrument;
};

export const FuturesMaxPosition = ({ selectedAccountOrClient, instrument }: FuturesMaxPositionProps) => {
  const { translation } = useLocale();

  return (
    <AccordionDetails>
      <span>{translation.orderModal.transactionInfo.maxPosition}</span>
      <MoneyMaskHOC
        isInverse
        size={100}
      >
        <FuturesMaxPositionValue
          selectedAccountOrClient={selectedAccountOrClient}
          instrument={instrument}
        />
      </MoneyMaskHOC>
    </AccordionDetails>
  );
};
